import { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import { AuthContext } from "../../Auth/AuthProvider";
import { useModal } from "../../contexts/ModalContext";
import useApi from "../../hooks/useApi";
import { VerifyPermission } from "../../Funcoes/VerifyPermission";

import { formatarData } from "../../Funcoes/FormatarData";
import { formatCPF } from "../../Funcoes/FormatCPF";
import { formatValorMoney } from "../../Funcoes/FormatValorMoney";

import Overlay from "../../Componentes/Overlay";
import SideMenu from "../../Componentes/SideMenu";
import SideMenuBtn from "../../Componentes/SideMenuBtn";

import ProfileHeader from "./ProfileHeader";
import InfoCard from "./InfoCard";
import InfoField from "./InfoField";
import FolgasCard from "./FolgasCard";

import ModalEditInfo from "../Colaborador/ModalEditInfo";
import ModalEditAloj from "../Colaborador/ModalEditAloj";
import ModalEditLocacao from "../Colaborador/ModalEditLocacao";
import ModalBeneficios from "../Colaborador/ModalBeneficios";
import ModalAddFolga from "../Colaborador/ModalAddFolga";

const Page = styled.div`
    width: 100%;
    height: calc(100% - 70px);
    background-color: #242D34;
    box-sizing: border-box;
    overflow-y: auto;
    padding: 16px;

    @media (max-width: 768px) {
        padding: 12px;
    }
`;

const Content = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const CardsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const money = (valor) => "R$ " + (valor ? formatValorMoney(valor) : formatValorMoney("0.00"));

const ContentContainer = () => {

    const { user } = useContext(AuthContext);
    const { matricula } = useParams();
    const { isModalOpen, closeModal, openModal } = useModal();

    const api = useApi();

    const [loading, setLoading] = useState(true);
    const [colabInfo, setColabInfo] = useState({});

    const fetchColab = async () => {
        try {
            const { data } = await api.get(`/Painel/API/Colaborador/GetInfo/index.php?matricula=${matricula}`);
            setColabInfo(data);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchColab();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matricula])

    const protheus = colabInfo.protheus;
    const custom = colabInfo.custom;

    const podeVerBeneficios = VerifyPermission(user, { modulo: "rh", submodulo: "colaborador", funcao: "view_benefits" });
    const podeAddFolga = VerifyPermission(user, { modulo: "rh", submodulo: "colaborador", funcao: "add_manual_folga" });

    const openBenefts = () => {
        closeModal();
        openModal('tabelaBeneficios');
    }

    const openFolgaCampo = () => {
        closeModal();
        openModal('addNewFolga');
    }

    const contato = protheus?.TELEFONE
        ? `${protheus?.DDDFONE ? `(${protheus.DDDFONE}) ` : ""}${protheus.TELEFONE}`
        : "-";

    return (
        <>
            <Page>
                <Content>
                    <ProfileHeader data={protheus} loading={loading} />

                    <CardsGrid>
                        <InfoCard title="DADOS PESSOAIS">
                            <InfoField loading={loading} title="Matrícula" value={protheus?.MATRICULA || '-'} />
                            <InfoField loading={loading} title="Nome" value={protheus?.NOME || '-'} />
                            <InfoField loading={loading} title="CPF" value={protheus?.CPF ? formatCPF(protheus.CPF) : "-"} />
                            <InfoField loading={loading} title="Centro de custo" value={protheus?.CENTRO_CUSTO || '-'} />
                            <InfoField loading={loading} title="Função" value={protheus?.FUNCAO || '-'} />
                            <InfoField loading={loading} title="Situação" value={protheus?.STATUS || '-'} />
                            <InfoField loading={loading} title="Data de admissão" value={protheus?.ADMISSAO ? formatarData(protheus.ADMISSAO) : "-"} />
                            <InfoField loading={loading} title="Data de demissão" value={protheus?.DEMISSAO ? formatarData(protheus.DEMISSAO) : "-"} />
                            <InfoField loading={loading} title="Mão de obra" value={protheus?.MAO_OBRA || '-'} />
                            <InfoField loading={loading} title="Sexo" value={protheus?.SEXO || '-'} />
                            <InfoField loading={loading} title="Data de nascimento" value={protheus?.DATA_NASC ? formatarData(protheus.DATA_NASC) : '-'} />
                            <InfoField loading={loading} title="RG" value={protheus?.RG || '-'} />
                            <InfoField loading={loading} title="UF do RG" value={protheus?.RG_UF || '-'} />
                            <InfoField loading={loading} title="Município de residência" value={protheus?.MUNICIPIO ? `${protheus.MUNICIPIO}/${protheus.ESTADO}` : '-'} />
                            <InfoField loading={loading} title="PIS" value={protheus?.PIS || '-'} />
                            <InfoField loading={loading} title="Contato" value={contato} />
                        </InfoCard>

                        <InfoCard
                            title="INFORMAÇÕES DO COLABORADOR"
                            showEdit={!loading}
                            onEdit={() => openModal('editColabInfo')}
                        >
                            <InfoField loading={loading} title="Crachá de acesso" value={custom?.cracha || '-'} />
                            <InfoField loading={loading} title="Contrato" value={custom?.contrato || '-'} />
                            <InfoField loading={loading} title="Disciplina" value={custom?.disciplina || '-'} />
                            <InfoField loading={loading} title="Gerente" value={custom?.nome_gerente || '-'} />
                            <InfoField loading={loading} title="Coordenador" value={custom?.nome_coordenador || '-'} />
                            <InfoField loading={loading} title="Supervisor" value={custom?.nome_sup || '-'} />
                            <InfoField loading={loading} title="Técnico" value={custom?.nome_tecnico || '-'} />
                            <InfoField loading={loading} title="Encarregado" value={custom?.nome_enc || '-'} />
                            <InfoField loading={loading} title="Hospedagem" value={custom?.hospedagem || '-'} />
                            <InfoField loading={loading} title="Emprestado" value={custom?.locado || '-'} />
                            <InfoField loading={loading} title="Valor cartão V.A" value={money(custom?.valor_cartao_va)} />
                            <InfoField loading={loading} title="Valor moradia" value={money(custom?.valor_moradia)} />
                            <InfoField loading={loading} title="Folga de campo" value={custom?.tem_folga ? 'SIM' : 'NÃO'} />
                        </InfoCard>

                        {custom?.hospedagem === 'ALOJADO' && (
                            <InfoCard
                                title="INFORMAÇÕES DO ALOJAMENTO"
                                showEdit={!loading}
                                onEdit={() => openModal('editColabAloj')}
                            >
                                <InfoField loading={loading} title="Alojamento" value={custom?.republica || '-'} />
                                <InfoField loading={loading} title="Quarto" value={custom?.quarto_rep || '-'} />
                                <InfoField loading={loading} title="Check-in" value={custom?.check_in ? formatarData(custom.check_in) : '-'} />
                                <InfoField loading={loading} title="Check-out" value={custom?.check_out ? formatarData(custom.check_out) : '-'} />
                            </InfoCard>
                        )}

                        {custom?.locado && (
                            <InfoCard
                                title="INFORMAÇÕES DE LOCAÇÃO"
                                showEdit={!loading}
                                onEdit={() => openModal('editModalLocado')}
                            >
                                <InfoField loading={loading} title="Alojamento" value={custom?.rep_temp || '-'} />
                                <InfoField loading={loading} title="Quarto" value={custom?.quarto_rep_temp || '-'} />
                            </InfoCard>
                        )}
                    </CardsGrid>

                    <FolgasCard
                        folgasCampo={colabInfo.folgas}
                        fetchData={fetchColab}
                        protheus={protheus}
                    />
                </Content>
            </Page>

            {isModalOpen('sidebarColaborador') && (
                <Overlay state={true} changeContent={closeModal}>
                    <SideMenu>
                        {podeVerBeneficios && (
                            <SideMenuBtn button="BENEFÍCIOS" clickFunc={openBenefts} />
                        )}
                        {podeAddFolga && (
                            <SideMenuBtn button="FOLGA DE CAMPO" clickFunc={openFolgaCampo} />
                        )}
                    </SideMenu>
                </Overlay>
            )}

            {isModalOpen('editColabInfo') && (
                <ModalEditInfo info={custom} fetchData={fetchColab} />
            )}

            {isModalOpen('editColabAloj') && (
                <ModalEditAloj info={custom} fetchData={fetchColab} />
            )}

            {isModalOpen('editModalLocado') && (
                <ModalEditLocacao info={custom} fetchData={fetchColab} />
            )}

            {isModalOpen('tabelaBeneficios') && podeVerBeneficios && (
                <ModalBeneficios />
            )}

            {isModalOpen('addNewFolga') && podeAddFolga && (
                <ModalAddFolga info={protheus} fetchData={fetchColab} />
            )}
        </>
    )
}

export default ContentContainer;
